import { useEffect, useRef } from 'react';

const SCRIPT_ID = 'google-translate-script';
const CONTAINER_ID = 'google_translate_element';

const loadGoogleScript = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Translate requires a browser environment'));
  }

  const existingScript = document.getElementById(SCRIPT_ID);
  if (existingScript) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Translate script'));
    document.body.appendChild(script);
  });
};

const initTranslateElement = (containerSelector, options = {}) => {
  if (!window.google || !window.google.translate || !window.google.translate.TranslateElement) {
    return;
  }

  const container = document.querySelector(containerSelector);
  if (!container) {
    return;
  }

  container.innerHTML = '';

  const defaultOptions = {
    pageLanguage: 'es',
    includedLanguages: 'en,es,ca',
    layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
    autoDisplay: false,
  };

  new window.google.translate.TranslateElement({ ...defaultOptions, ...options }, containerSelector.substring(1));
};

const GoogleTranslate = ({
  includedLanguages = 'en,es,ca',
  pageLanguage = 'es',
  wrapperClassName = 'google-translate-wrapper',
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    window.googleTranslateElementInit = () => {
      initTranslateElement(`#${CONTAINER_ID}`, { pageLanguage, includedLanguages });
    };

    let cancelled = false;

    loadGoogleScript()
      .then(() => {
        if (cancelled) {
          return;
        }
        window.googleTranslateElementInit();
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      cancelled = true;
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [includedLanguages, pageLanguage]);

  return (
    <div className={wrapperClassName}>
      <div id={CONTAINER_ID} ref={containerRef} />
    </div>
  );
};

export default GoogleTranslate;
